import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChefHat, UtensilsCrossed, Users, Star } from "lucide-react";

const Welcome = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fef8f3]">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(/food-hero.jpg)` }}
        />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <ChefHat className="w-10 h-10 text-orange-500" />
                <span className="text-lg font-medium text-gray-500">Welcome to</span>
              </div>
              
              <h1 className="text-6xl md:text-8xl font-bold text-gray-900 mb-6">
                Foodify
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-500 mb-10 leading-relaxed max-w-xl">
                The social platform where food lovers discover restaurants, share culinary experiences, and connect with fellow foodies.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate("/register")}
                  className="h-14 px-8 text-lg font-semibold bg-gradient-to-r from-orange-500 via-orange-500 to-yellow-500 hover:opacity-90 transition-opacity shadow-[0_8px_30px_-6px_rgba(249,115,22,0.3)] text-white rounded-lg"
                >
                  Get Started
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="h-14 px-8 text-lg font-semibold border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-colors rounded-lg"
                >
                  Sign In
                </button>
              </div>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20"
            >
              {[
                {
                  icon: UtensilsCrossed,
                  title: "Discover",
                  description: "Find the best restaurants and hidden gems near you",
                },
                {
                  icon: Star,
                  title: "Review",
                  description: "Share your honest opinions and food photography",
                },
                {
                  icon: Users,
                  title: "Connect",
                  description: "Follow foodies and chefs with similar taste",
                },
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="p-6 rounded-2xl bg-white shadow-sm border border-gray-200"
                >
                  <feature.icon className="w-10 h-10 text-orange-500 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500">{feature.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Decorative Elements */}
        <motion.div
          animate={{ y: [-15, 15, -15] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-20 top-32 hidden lg:block"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-orange-500 via-orange-500 to-yellow-500 opacity-20" />
        </motion.div>
        <motion.div
          animate={{ y: [10, -10, 10] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute right-40 bottom-40 hidden lg:block"
        >
          <div className="w-16 h-16 rounded-full bg-yellow-400 opacity-30" />
        </motion.div>
      </div>
    </div>
  );
};

export default Welcome;
